import { useEffect, useState } from "react";
import { X, Plus, Trash2, FileDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const CURRENCIES = ["USD", "EUR", "AED", "SAR", "QAR"];

function emptyItem() {
  return { name: "", description: "", qty: 1, unit_price: 0 };
}

export default function QuoteBuilder({ open, onClose, opportunity, products, companies }) {
  const [items, setItems] = useState([]);
  const [discountPct, setDiscountPct] = useState(0);
  const [vatPct, setVatPct] = useState(5);
  const [currency, setCurrency] = useState("USD");
  const [quoteNumber, setQuoteNumber] = useState("");
  const [busy, setBusy] = useState(false);

  // Pre-populate items from opportunity products on open
  useEffect(() => {
    if (!open || !opportunity) return;
    const linked = (opportunity.product_ids || [])
      .map((pid) => products.find((p) => p.id === pid))
      .filter(Boolean);
    if (linked.length) {
      setItems(linked.map((p) => ({
        name: p.name,
        description: p.description || "",
        qty: 1,
        unit_price: p.typical_price_usd || 0,
      })));
    } else {
      // Single line from opp value
      setItems([{
        name: opportunity.title || "Custom scope",
        description: "",
        qty: 1,
        unit_price: opportunity.value_usd || 0,
      }]);
    }
    setDiscountPct(0);
    setVatPct(5);
    setCurrency("USD");
    setQuoteNumber("");
  }, [open, opportunity, products]);

  if (!open || !opportunity) return null;

  const update = (i, key, value) => {
    setItems((prev) => prev.map((it, idx) =>
      idx === i ? { ...it, [key]: value } : it
    ));
  };

  const removeItem = (i) => {
    setItems((prev) => prev.filter((_, idx) => idx !== i));
  };

  const addItem = (preset) => {
    setItems((prev) => [...prev, preset || emptyItem()]);
  };

  const subtotal = items.reduce((a, b) => a + (b.qty || 0) * (b.unit_price || 0), 0);
  const discount = subtotal * (discountPct / 100);
  const afterDisc = subtotal - discount;
  const vat = afterDisc * (vatPct / 100);
  const total = afterDisc + vat;

  const fmt = (v) => `${currency} ${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const company = companies.find((c) => c.id === opportunity.company_id);

  const generate = async () => {
    if (items.length === 0) {
      toast.error("Add at least one line item");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(
        `/opportunities/${opportunity.id}/quote_pdf`,
        {
          discount_pct: parseFloat(discountPct) || 0,
          vat_pct: parseFloat(vatPct) || 0,
          currency,
          items: items.map((it) => ({
            name: it.name,
            description: it.description,
            qty: parseFloat(it.qty) || 1,
            unit_price: parseFloat(it.unit_price) || 0,
          })),
          quote_number: quoteNumber || null,
        },
        { responseType: "blob" }
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Quote PDF ready");
      onClose();
    } catch (err) {
      toast.error("Failed to generate quote");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-2 sm:p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="quote-builder"
        className="bg-white border border-gray-200 w-full max-w-4xl max-h-[95vh] overflow-y-auto"
      >
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="label-eyebrow">Quote builder</div>
            <div className="font-display text-xl font-bold mt-1">{opportunity.title}</div>
            {company && <div className="text-xs text-gray-500 mt-0.5">For: {company.name}</div>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-black"><X size={20} /></button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          {/* Meta row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="label-eyebrow block mb-2">Quote # (auto if empty)</label>
              <input className="input-stark" value={quoteNumber}
                onChange={(e) => setQuoteNumber(e.target.value)}
                placeholder="SOD-UAE-…"
                data-testid="quote-number-input" />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Currency</label>
              <select className="input-stark" value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="quote-currency-select">
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Discount %</label>
              <input className="input-stark" type="number" value={discountPct}
                onChange={(e) => setDiscountPct(e.target.value)}
                data-testid="quote-discount-input" />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">VAT %</label>
              <input className="input-stark" type="number" value={vatPct}
                onChange={(e) => setVatPct(e.target.value)}
                data-testid="quote-vat-input" />
            </div>
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow">Line items · {items.length}</div>
              <button onClick={() => addItem()} className="btn-ghost text-xs" data-testid="add-line-item">
                <Plus size={12} /> Add line
              </button>
            </div>

            <div className="border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm" data-testid="line-items-table">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
                    <th className="text-left p-2 w-10">#</th>
                    <th className="text-left p-2 min-w-[200px]">Description</th>
                    <th className="text-right p-2 w-20">Qty</th>
                    <th className="text-right p-2 w-32">Unit price</th>
                    <th className="text-right p-2 w-32">Subtotal</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} className="border-b border-gray-100" data-testid={`line-item-${i}`}>
                      <td className="p-2 text-gray-400">{i + 1}</td>
                      <td className="p-2">
                        <input className="input-stark text-sm mb-1" placeholder="Product / item name"
                          value={it.name} onChange={(e) => update(i, "name", e.target.value)} />
                        <textarea className="input-stark text-xs min-h-[40px]" placeholder="Description (optional)"
                          value={it.description} onChange={(e) => update(i, "description", e.target.value)} />
                      </td>
                      <td className="p-2">
                        <input className="input-stark text-sm text-right" type="number" step="any"
                          value={it.qty} onChange={(e) => update(i, "qty", e.target.value)} />
                      </td>
                      <td className="p-2">
                        <input className="input-stark text-sm text-right" type="number" step="any"
                          value={it.unit_price} onChange={(e) => update(i, "unit_price", e.target.value)} />
                      </td>
                      <td className="p-2 text-right font-semibold tabular-nums">
                        {fmt((parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0))}
                      </td>
                      <td className="p-2 text-center">
                        <button onClick={() => removeItem(i)} className="text-gray-400 hover:text-red-600">
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-gray-400 text-sm">No line items. Click "Add line".</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Quick add from catalog */}
            {products.length > 0 && (
              <div className="mt-3">
                <div className="label-eyebrow mb-2">Quick add from catalog</div>
                <div className="flex flex-wrap gap-1">
                  {products.slice(0, 12).map((p) => (
                    <button
                      key={p.id}
                      onClick={() => addItem({
                        name: p.name,
                        description: p.description || "",
                        qty: 1,
                        unit_price: p.typical_price_usd || 0,
                      })}
                      className="text-[11px] border border-gray-200 hover:border-[#004B87] hover:text-[#004B87] px-2 py-1 transition-colors"
                    >
                      + {p.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="border-t border-gray-200 pt-4 flex justify-end">
            <div className="w-full sm:w-72 space-y-1.5 text-sm tabular-nums">
              <div className="flex justify-between">
                <span className="text-gray-500">Subtotal</span><span>{fmt(subtotal)}</span>
              </div>
              {discountPct > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Discount ({discountPct}%)</span>
                  <span className="text-red-600">- {fmt(discount)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">VAT ({vatPct}%)</span><span>{fmt(vat)}</span>
              </div>
              <div className="flex justify-between bg-[#004B87] text-white px-3 py-2 mt-2 font-display font-bold">
                <span>Total</span><span data-testid="quote-total">{fmt(total)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={generate} disabled={busy} className="btn-primary" data-testid="generate-quote-pdf">
            <FileDown size={14} /> {busy ? "Generating…" : "Generate PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}
